/**
 * Kommentare am Vorgang (TEAM-CHAT.md, Abschnitt 3).
 *
 * Dauerhaft sichtbar in der rechten Spalte des Angebots- und des
 * Auftrags-Detail – nicht hinter einem Reiter. Ein Faden, den man erst
 * aufklappen muss, wird nicht gelesen, und dann schreibt wieder jeder
 * woanders.
 *
 * Gegenüber dem Chat fehlen zwei Dinge: der Lesehaken und der
 * Anwesenheitspunkt. Am Vorgang zählt, was gesagt wurde, nicht wer gerade
 * online ist.
 *
 * Die Chronik daneben bleibt unberührt. Sie protokolliert maschinell; das
 * hier ist freier Text von Kollegen. Beides zu mischen macht den Nachweis
 * unzuverlässig und das Gespräch unauffindbar.
 */

import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api';
import { useKora } from '../zustand';
import { Beitrag } from './Beitrag';
import { Verfasser, type Anhangwahl } from './Verfasser';
import type { Beitrag as BeitragDaten, ChatUeberblick, Gespraech } from '../typen';
import './chat.css';

export function Kommentarfaden({
  angebotId,
  /** „Angebot" oder „Auftrag" – nur Platzhalter und Ablagepfad hängen daran. */
  art,
  ordner,
}: {
  angebotId: number;
  art: 'Angebot' | 'Auftrag';
  /** Lesbarer Ablageort für die Anhangkarte, etwa „Angebote/26014_FE_Weitlofgasse". */
  ordner: string;
}) {
  const { nutzer } = useKora();
  const queryClient = useQueryClient();
  const [antwortAuf, setAntwortAuf] = useState<BeitragDaten | null>(null);
  const [sucheOffen, setSucheOffen] = useState(false);
  const [suche, setSuche] = useState('');
  const frage = sucheOffen ? suche.trim() : '';
  const ende = useRef<HTMLDivElement>(null);

  /*
   * Am Vorgang ist die Suche Pflicht, nicht Komfort (TEAM-CHAT.md, 13):
   * Nach der Übernahme des Altbestands hängen an manchen Vorgängen Hunderte
   * alte Kommentare. Deshalb liegt hier immer der ganze Faden offen – nicht nur
   * der laufende Tag wie im Chat.
   */
  const gespraech = useQuery({
    queryKey: ['kommentare', angebotId, frage],
    queryFn: () =>
      api.hole<Gespraech>(
        `/chat/gespraech?angebotId=${angebotId}&alle=1` + (frage ? `&q=${encodeURIComponent(frage)}` : ''),
      ),
    refetchInterval: frage ? false : 30_000,
  });

  /* Die Personenliste für den @-Knopf kommt aus demselben Überblick wie der Zähler. */
  const ueberblick = useQuery({
    queryKey: ['chat-ueberblick', ''],
    queryFn: () => api.hole<ChatUeberblick>('/chat/ueberblick'),
    staleTime: 60_000,
  });

  const gelesen = useMutation({
    mutationFn: () => api.sende<{ ok: boolean }>('/chat/gelesen', { angebotId }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['chat-ueberblick'] });
      void queryClient.invalidateQueries({ queryKey: ['mein-tag'] });
    },
  });
  /*
   * Wer die Maske offen hat, hat gelesen – aber erst nach einem Moment.
   * Sofort abzuhaken würde die Trennlinie „Neu" wegnehmen, bevor das Auge
   * sie gefunden hat.
   */
  const schonGelesen = useRef(0);
  useEffect(() => {
    const offen =
      gespraech.data?.gruppen.flatMap((g) => g.beitraege).filter((b) => !b.gelesen && !b.eigen).length ?? 0;
    if (offen && schonGelesen.current !== angebotId) {
      schonGelesen.current = angebotId;
      const t = window.setTimeout(() => gelesen.mutate(), 1500);
      return () => window.clearTimeout(t);
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gespraech.data, angebotId]);

  const senden = useMutation({
    mutationFn: (e: { text: string; anhang: Anhangwahl | null }) =>
      api.sende<Gespraech>('/chat/beitrag', {
        angebotId,
        text: e.text,
        antwortAuf: antwortAuf?.id ?? null,
        anhang: e.anhang,
      }),
    onSuccess: (neu) => {
      queryClient.setQueryData(['kommentare', angebotId], neu);
      void queryClient.invalidateQueries({ queryKey: ['chat-ueberblick'] });
      void queryClient.invalidateQueries({ queryKey: ['mein-tag'] });
      setAntwortAuf(null);
      window.setTimeout(() => ende.current?.scrollIntoView({ block: 'end', behavior: 'smooth' }), 60);
    },
  });

  const aendern = useMutation({
    mutationFn: (e: { id: number; text: string }) =>
      api.aendere<{ ok: boolean }>(`/chat/beitrag/${e.id}`, { text: e.text }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['kommentare', angebotId] }),
  });

  const d = gespraech.data;
  const alle = d ? d.gruppen.flatMap((g) => g.beitraege) : [];
  const anzahl = alle.reduce((s, b) => s + 1 + b.antworten.length, 0);
  const ungelesen =
    alle.filter((b) => !b.gelesen && !b.eigen).length +
    alle.reduce((s, b) => s + b.antworten.filter((a) => !a.gelesen && !a.eigen).length, 0);

  return (
    <section className="kf">
      <div className="kf-kopf">
        <span className="ch-kicker">Kommentare · {frage ? `${anzahl} Treffer` : anzahl}</span>
        {ungelesen > 0 && !frage && <span className="kf-ungelesen">{ungelesen} ungelesen</span>}
        <button
          type="button"
          className={`ch-quadrat klein ${sucheOffen ? 'aktiv' : ''}`}
          title="In den Kommentaren suchen"
          aria-expanded={sucheOffen}
          onClick={() => {
            setSucheOffen((o) => !o);
            setSuche('');
          }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <circle cx="11" cy="11" r="7" />
            <path d="M20 20l-3.5-3.5" />
          </svg>
        </button>
      </div>

      {sucheOffen && (
        <div className="ch-suche">
          <input
            autoFocus
            value={suche}
            placeholder="In den Kommentaren suchen …"
            onChange={(e) => setSuche(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                setSuche('');
                setSucheOffen(false);
              }
            }}
          />
          {suche && (
            <button type="button" className="ch-such-x" title="Suche leeren" onClick={() => setSuche('')}>
              ×
            </button>
          )}
          <span className="ch-such-zahl">
            {!frage ? 'Tippen, um zu suchen' : `${anzahl} in ${d?.durchsucht ?? 0} Kommentaren`}
          </span>
        </div>
      )}

      <div className="kf-verlauf">
        {d && d.gruppen.length === 0 && (
          <span className="ch-leer">
            {frage
              ? `Kein Treffer für „${frage}" an diesem ${art}.`
              : `Noch kein Kommentar zu diesem ${art}. Was hier steht, bleibt am Vorgang.`}
          </span>
        )}
        {d?.gruppen.map((g) => (
          <div className="ch-tag" key={g.tag}>
            <span className="ch-tag-trenner">
              <span className="ch-tag-linie" />
              <span className="ch-tag-text">{g.label}</span>
              <span className="ch-tag-linie" />
            </span>
            {g.beitraege.map((b) => (
              <Beitrag
                key={b.id}
                b={b}
                ich={nutzer?.name ?? ''}
                neu={d.neuAb === b.id}
                suche={frage}
                onAntworten={setAntwortAuf}
                onAendern={(x, text) => aendern.mutate({ id: x.id, text })}
              />
            ))}
          </div>
        ))}
        <div ref={ende} />
      </div>

      {!frage && (
        <Verfasser
          platzhalter={`Kommentar zu diesem ${art} · @Name erwähnt jemanden`}
          knopf="Kommentieren"
          ablageHinweis={`Anhänge: Vorgangsordner · ${ordner}`}
          personen={(ueberblick.data?.personen ?? []).map((p) => p.name)}
          antwortAuf={antwortAuf}
          onAntwortWeg={() => setAntwortAuf(null)}
          onSenden={(text, anhang) => senden.mutate({ text, anhang })}
          onTippt={() => undefined}
        />
      )}

      <p className="kf-fuss">
        Kommentare bleiben am Vorgang und sind nicht löschbar. Zurufe ohne Vorgangsbezug gehören in
        den Team-Chat.
      </p>
    </section>
  );
}
